// Joints between dynamic bodies (spike 002, phase B, gate B5: jointed ragdolls, decision 0057). Two kinds:
//
// - spherical: a ball joint (the anchors held together, every turn free), with optional per-axis limits;
// - revolute: a hinge about one axis, with optional limits on its angle.
//
// Frames: a joint's anchors are given in each body's own frame, and both bodies' frames count as the joint's rest:
// the turn between them when their rotations are equal is zero. A game builds a ragdoll by creating every part with
// the same rotation (its pose's), so the limits are measured from that pose (the figure's rest).
//
// How the angles are read, matching Rapier's own limit check: the relative turn q = qA⁻¹ qB (with w ≥ 0), and per
// axis the angle 2·asin(q.axis). Rapier keeps sin(angle / 2) of each limited axis inside sin(limit / 2), softly: a
// violent impulse overshoots for a few steps. A revolute joint's angle is its twist about the hinge axis.
//
// Rapier's JS API sets a spherical joint's limits only through its raw joint set (`jointSetLimits`, per angular
// axis); `limitSpherical` does that in one place, so a Rapier upgrade that changes it breaks one function.
import type RAPIER from "@dimforge/rapier3d-compat";
import type { BodyId, Quat, Vec3 } from "./world";

/** A joint's stable game ID. */
export type JointId = string;

/** [min, max] radians, min ≤ 0 ≤ max, inside (-π, π). */
export type AngleRange = [number, number];

/** What both joint kinds share. */
interface JointBase {
  id: JointId;
  /** The parent body (e.g. the chest) and the child (e.g. the head). */
  a: BodyId;
  b: BodyId;
  /** The joint's point in A's frame and in B's frame (the same world point at rest). */
  anchorA: Vec3;
  anchorB: Vec3;
  /** Let the two bodies collide with each other. Default false (a ragdoll's neighbours overlap at the joint). */
  contacts?: boolean;
  /** Joint friction: a motor driving the relative turn's speed toward 0 with this factor (Rapier's velocity motor;
   *  0 or absent: none). A ragdoll's limbs come to rest instead of trembling against their limits. */
  friction?: number;
}

/** A ball joint; `limits` caps the turn about each of the frame's axes (absent axes are free). */
export interface SphericalJointSpec extends JointBase {
  kind: "spherical";
  limits?: { x?: AngleRange; y?: AngleRange; z?: AngleRange };
}

/** A hinge about `axis` (the same direction in both bodies' frames at rest), its angle capped by `limits`. */
export interface RevoluteJointSpec extends JointBase {
  kind: "revolute";
  axis: Vec3;
  limits?: AngleRange;
}

/** Any joint. */
export type JointSpec = SphericalJointSpec | RevoluteJointSpec;

/** A joint's angles now: per axis for a spherical joint ([x, y, z]), its hinge angle for a revolute one. */
export type JointAngles = { kind: "spherical"; angles: Vec3 } | { kind: "revolute"; angle: number };

const clamp1 = (v: number): number => Math.max(-1, Math.min(1, v));

/** The relative turn of B against A (qA⁻¹ qB), with w ≥ 0. */
export function relativeTurn(qa: Quat, qb: Quat): Quat {
  // conjugate of qa times qb
  const ax = -qa.x;
  const ay = -qa.y;
  const az = -qa.z;
  const aw = qa.w;
  const x = aw * qb.x + ax * qb.w + ay * qb.z - az * qb.y;
  const y = aw * qb.y - ax * qb.z + ay * qb.w + az * qb.x;
  const z = aw * qb.z + ax * qb.y - ay * qb.x + az * qb.w;
  const w = aw * qb.w - ax * qb.x - ay * qb.y - az * qb.z;
  const s = w < 0 ? -1 : 1;
  return { x: x * s, y: y * s, z: z * s, w: w * s };
}

/** A spherical joint's per-axis angles (radians), as Rapier's limits read them: 2·asin of the relative turn's x, y, z. */
export function sphericalAngles(qa: Quat, qb: Quat): Vec3 {
  const q = relativeTurn(qa, qb);
  return { x: 2 * Math.asin(clamp1(q.x)), y: 2 * Math.asin(clamp1(q.y)), z: 2 * Math.asin(clamp1(q.z)) };
}

/** A revolute joint's angle (radians): the relative turn's twist about the (unit) hinge axis. */
export function revoluteAngle(qa: Quat, qb: Quat, axis: Vec3): number {
  const q = relativeTurn(qa, qb);
  const m = Math.hypot(axis.x, axis.y, axis.z) || 1;
  const d = (q.x * axis.x + q.y * axis.y + q.z * axis.z) / m;
  let a = 2 * Math.atan2(d, q.w);
  if (a > Math.PI) a -= 2 * Math.PI;
  if (a < -Math.PI) a += 2 * Math.PI;
  return a;
}

/** How far (radians, ≥ 0) an angle lies outside a range; 0 inside it or with no range. */
export function outside(angle: number, range: AngleRange | undefined): number {
  if (!range) return 0;
  return Math.max(0, range[0] - angle, angle - range[1]);
}

/** How far a joint's angles lie past the limits given (the worst axis), radians. The limits are passed in, so a game
 *  can check a joint against the limits it asked for whatever Rapier holds. */
export function limitExcess(angles: JointAngles, limits: SphericalJointSpec["limits"] | AngleRange | undefined): number {
  if (!limits) return 0;
  if (angles.kind === "revolute") return Array.isArray(limits) ? outside(angles.angle, limits) : 0;
  if (Array.isArray(limits)) return 0;
  return Math.max(outside(angles.angles.x, limits.x), outside(angles.angles.y, limits.y), outside(angles.angles.z, limits.z));
}

/** Check a joint spec's numbers (ranges ordered, inside (-π, π), an axis that isn't zero). Throws with the joint's ID. */
export function checkJointSpec(s: JointSpec): void {
  const bad = (why: string): never => {
    throw new Error(`collision world: joint "${s.id}" ${why}`);
  };
  const range = (r: AngleRange | undefined, name: string): void => {
    if (!r) return;
    if (!(r[0] <= r[1])) bad(`has ${name} limits out of order (${r[0]}, ${r[1]})`);
    if (!(r[0] > -Math.PI && r[1] < Math.PI)) bad(`has ${name} limits outside (-π, π)`);
  };
  if (s.a === s.b) bad("joins a body to itself");
  if (s.kind === "spherical") {
    range(s.limits?.x, "x");
    range(s.limits?.y, "y");
    range(s.limits?.z, "z");
  } else {
    if (!(Math.hypot(s.axis.x, s.axis.y, s.axis.z) > 1e-9)) bad("has a zero hinge axis");
    range(s.limits, "hinge");
  }
}

/** Rapier's joint data for a spec. */
export function jointData(R: typeof RAPIER, s: JointSpec): RAPIER.JointData {
  if (s.kind === "spherical") return R.JointData.spherical(s.anchorA, s.anchorB);
  const m = Math.hypot(s.axis.x, s.axis.y, s.axis.z);
  return R.JointData.revolute(s.anchorA, s.anchorB, { x: s.axis.x / m, y: s.axis.y / m, z: s.axis.z / m });
}

/** The raw joint set's limit and motor setters (Rapier's JS API has no spherical-limit method, and hands spherical joints back without their motor methods). */
interface RawJointSet {
  jointSetLimits(handle: number, axis: number, min: number, max: number): void;
  jointConfigureMotorVelocity(handle: number, axis: number, targetVel: number, factor: number): void;
}

/** Apply a spec's limits and friction to a joint Rapier has created. */
export function applyLimits(R: typeof RAPIER, joint: RAPIER.ImpulseJoint, s: JointSpec): void {
  const f = s.friction ?? 0;
  if (s.kind === "revolute") {
    const hinge = joint as RAPIER.RevoluteImpulseJoint;
    if (s.limits) hinge.setLimits(s.limits[0], s.limits[1]);
    if (f > 0) hinge.configureMotorVelocity(0, f);
    return;
  }
  const raw = (joint as unknown as { rawSet: RawJointSet }).rawSet;
  if (f > 0) for (const axis of [R.JointAxis.AngX, R.JointAxis.AngY, R.JointAxis.AngZ]) raw.jointConfigureMotorVelocity(joint.handle, axis, 0, f);
  const axes: [keyof NonNullable<SphericalJointSpec["limits"]>, number][] = [
    ["x", R.JointAxis.AngX],
    ["y", R.JointAxis.AngY],
    ["z", R.JointAxis.AngZ],
  ];
  for (const [k, axis] of axes) {
    const r = s.limits?.[k];
    if (r) raw.jointSetLimits(joint.handle, axis, r[0], r[1]);
  }
}
